// How much of the first message is kept as a chat's provisional title
export const MAX_CHAT_NAME_LENGTH = 60;

// Only break on a word boundary if it leaves a title worth reading
const MIN_WORD_BOUNDARY_LENGTH = 20;

const ELLIPSIS = "…";

/**
 * Provisional title for a new chat, taken from its first message.
 * Replaced by an AI-generated title once ChatHistoryDropdown runs.
 *
 * Trimmed on a word boundary so the title never ends mid-word, with a trailing
 * ellipsis making the truncation obvious in the chat list and its tooltip.
 */
export const getProvisionalChatName = (firstMessage) => {
  if (!firstMessage) {
    return "";
  }

  const trimmedMessage = firstMessage.trim();
  if (trimmedMessage.length <= MAX_CHAT_NAME_LENGTH) {
    return trimmedMessage;
  }

  const truncated = trimmedMessage.slice(0, MAX_CHAT_NAME_LENGTH);
  const lastSpaceIndex = truncated.lastIndexOf(" ");
  const onWordBoundary =
    lastSpaceIndex >= MIN_WORD_BOUNDARY_LENGTH ? truncated.slice(0, lastSpaceIndex) : truncated;

  return `${onWordBoundary.trimEnd()}${ELLIPSIS}`;
};

/**
 * True while a chat still carries a placeholder title and so needs an AI-generated
 * one. Two placeholders exist: the legacy client hostname, and the truncated first
 * message used for chats created after that was replaced.
 *
 * Derived from values already stored on the chat rather than a saved flag, because
 * the backend persists a fixed field list and would drop a new one, leaving chats
 * unnamed on any device that did not create them.
 */
export const isProvisionalChatName = ({ chatName, clientHostname, firstMessage }) => {
  if (!chatName) {
    return true;
  }
  if (chatName === clientHostname || chatName === getProvisionalChatName(firstMessage)) {
    return true;
  }
  // Chats titled before word-boundary trimming carry a raw slice of the first message
  return Boolean(firstMessage) && chatName === firstMessage.slice(0, MAX_CHAT_NAME_LENGTH);
};
